import { Field } from "@ui/components/Field";
import { resolveValidationState } from "@ui/components/shared/resolveValidationState";
import { TextField, type TextFieldProps, TextFieldTextArea } from "@ui/primitives/TextField";
import type { ReactNode } from "react";

export interface StandardTextAreaProps extends TextFieldProps {
  label: string;
  /** Whether the label is visually hidden (screen-reader only) */
  isLabelHidden?: boolean;
  /** Whether the field is optional — appends a styled "(optional)" hint to the label */
  isOptional?: boolean;
  leadIcon?: ReactNode;
  placeholder?: string;
  description?: ReactNode;
  errorMessage?: ReactNode;
  successMessage?: ReactNode;
  /** Styles the field's outer box — see StandardTextField's `className`. */
  className?: string;
}

const textareaClassName =
  "min-h-20 flex-1 resize-none bg-transparent caret-blue-400 text-base/6 text-neutral-900 outline-none placeholder:text-neutral-400";

export function StandardTextArea({
  label,
  isLabelHidden,
  isOptional,
  leadIcon,
  placeholder,
  description,
  errorMessage,
  successMessage,
  className,
  ...props
}: StandardTextAreaProps) {
  const validationState = resolveValidationState(props.isInvalid, successMessage);
  const showErrorMessage = props.isInvalid && errorMessage;

  return (
    <Field className={className} isInvalid={props.isInvalid} isOptional={isOptional}>
      <TextField {...props} className="contents">
        <Field.Label isHidden={isLabelHidden}>{label}</Field.Label>
        <Field.Control align="start" asGroup leadIcon={leadIcon} validationState={validationState}>
          <TextFieldTextArea className={textareaClassName} placeholder={placeholder} />
        </Field.Control>
        {showErrorMessage ? (
          <Field.Error>{errorMessage}</Field.Error>
        ) : successMessage ? (
          <Field.Success>{successMessage}</Field.Success>
        ) : description ? (
          <Field.Description>{description}</Field.Description>
        ) : null}
      </TextField>
    </Field>
  );
}
